import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import '../styles/EquipmentDetail.css';
import { updatePageSEO, addStructuredData } from '../utils/SeoUtils';

// Combined equipment data for both munck and guindaste
const munckEquipment = [
  {
    name: 'MUNCK CONVENCIONAL',
    specs: 'Modelo 45 ton.metro - 4 lanças hidráulicas - 3 manuais',
    slug: 'munck-convencional',
    image: '/images/equip/munck/munckConvencional.png',
    graficos: ['/images/equip/munck/grafico/munckConvencional_g1.png'],
    category: 'Munck',
  },
  {
    name: 'MUNCK ESPECIAL E8',
    specs: 'Modelo 46 ton.metro - 8 lanças hidráulicas - 1 manual',
    slug: 'munck-especial-e8',
    image: '/images/equip/munck/munckEspecialE8_3.png',
    graficos: ['/images/equip/munck/grafico/munckEspecialE8_g1.png'],
    category: 'Munck',
  },
  {
    name: 'MUNCK ESPECIAL FLYJIB',
    specs: 'Modelo 62 ton.metro - 5 lanças hidráulicas - FLYJIB com 4 hidráulicas - 2 manuais',
    slug: 'munck-especial-flyjib',
    image: '/images/equip/munck/munckEspecialFLYJIB.png',
    graficos: ['/images/equip/munck/grafico/munckEspecialFLYJIB_g1.png'],
    category: 'Munck',
  },
];

const guindasteEquipment = [
  {
    name: 'GUINDASTE 70 TONELADAS',
    specs: 'Modelo TEREX LT1070 - 59 metros de lança',
    slug: 'guindaste-70-toneladas',
    image: '/images/equip/guindaste/guindaste70.png',
    graficos: [
      '/images/equip/guindaste/grafico/guindaste70_g1.png',
      '/images/equip/guindaste/grafico/guindaste70_g2.png',
      '/images/equip/guindaste/grafico/guindaste70_g3.png',
    ],
    category: 'Guindaste',
  },
  {
    name: 'GUINDASTE 90 TONELADAS ZOOMLION',
    specs: 'Modelo ZOOMLION ZMC900V5 - 65 metros de lança',
    slug: 'guindaste-90-toneladas-zoomlion',
    image: '/images/equip/guindaste/guindaste90_zoomlion.png',
    graficos: [
      '/images/equip/guindaste/grafico/guindaste90_zoomlion_g1.png',
      '/images/equip/guindaste/grafico/guindaste90_zoomlion_g2.png',
      '/images/equip/guindaste/grafico/guindaste90_zoomlion_g3.png',
    ],
    category: 'Guindaste',
  },
  {
    name: 'GUINDASTE 90 TONELADAS SANY',
    specs: 'Modelo SANY STC900T5 - 76,5 metros de lança',
    slug: 'guindaste-90-toneladas-sany',
    image: '/images/equip/guindaste/guindaste90_sany.png',
    graficos: [
      '/images/equip/guindaste/grafico/guindaste90_sany_g1.png',
      '/images/equip/guindaste/grafico/guindaste90_sany_g2.png',
    ],
    category: 'Guindaste',
  },
];

const allEquipment = [...munckEquipment, ...guindasteEquipment];

function DynamicEquipmentDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Determine equipment type from URL path
  const isMunckPage = location.pathname.startsWith('/munck');
  const isGuindastePage = location.pathname.startsWith('/guindaste');

  useEffect(() => {
    window.scrollTo(0, 0);

    const equipment = allEquipment.find((item) => item.slug === slug);

    if (equipment) {
      // Dynamic SEO based on equipment type and specific item
      const equipmentType = equipment.category.toLowerCase();
      const seoTitle = `${equipment.name} - Locação em Rondonópolis | MEGATON`;
      const seoDescription = `Aluguel de ${equipment.name} em Rondonópolis. ${equipment.specs}. Operadores certificados, equipamentos revisados. Solicite orçamento!`;
      const seoKeywords = `${equipment.name}, locação ${equipmentType}, aluguel ${equipmentType}, rondonópolis, ${equipment.slug}`;

      updatePageSEO(seoTitle, seoDescription, seoKeywords);

      // Add structured data for this specific equipment
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: equipment.name,
        description: equipment.specs,
        image: `https://megatonguindastes.com.br${equipment.image}`,
        url: `https://megatonguindastes.com.br/${equipmentType}/${equipment.slug}`,
        category: equipment.category,
        brand: {
          '@type': 'Brand',
          name: 'MEGATON',
        },
        manufacturer: {
          '@type': 'Organization',
          name: 'MEGATON',
        },
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceCurrency: 'BRL',
          seller: {
            '@type': 'Organization',
            name: 'MEGATON Serviços de Munck e Guindaste',
          },
        },
      };

      addStructuredData(structuredData);
    }
  }, [slug, location.pathname]);

  const equipment = allEquipment.find((item) => item.slug === slug);

  if (!equipment) {
    const backUrl = isMunckPage ? '/munck' : isGuindastePage ? '/guindaste' : '/';
    const backText = isMunckPage
      ? 'Voltar para Muncks'
      : isGuindastePage
        ? 'Voltar para Guindastes'
        : 'Voltar para Equipamentos';

    return (
      <div className="equipment-detail">
        <div className="container">
          <h1>Equipamento não encontrado</h1>
          <Link to={backUrl} className="back-btn">
            {backText}
          </Link>
        </div>
      </div>
    );
  }

  // Determine back URL based on current path
  const getBackUrl = () => {
    if (isMunckPage) return '/munck';
    if (isGuindastePage) return '/guindaste';
    return '/';
  };

  const getBackText = () => {
    if (isMunckPage) return '← Voltar para Muncks';
    if (isGuindastePage) return '← Voltar para Guindastes';
    return '← Voltar para Equipamentos';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === equipment.graficos.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? equipment.graficos.length - 1 : prev - 1));
  };

  return (
    <div className="equipment-detail">
      <section className="equipment-detail-hero">
        <div className="container">
          <Link to={getBackUrl()} className="back-btn">
            {getBackText()}
          </Link>
          <div className="equipment-detail-content">
            <div className="equipment-main-image">
              <img src={equipment.image} alt={equipment.name} loading="lazy" decoding="async" />
            </div>
            <div className="equipment-detail-info">
              <h1>{equipment.name}</h1>
              <p className="equipment-detail-specs">{equipment.specs}</p>
            </div>
          </div>
        </div>
      </section>

      {equipment.category === 'Munck' && (
        <section className="equipment-specs">
          <div className="container">
            <h2>Especificações Técnicas</h2>
            <div className="specs-grid">
              {equipment.slug === 'munck-convencional' && (
                <>
                  <div className="spec-item">
                    <h4>Capacidade no Pé</h4>
                    <p>
                      4,2 metros: <strong>11 ton</strong>
                    </p>
                  </div>
                  <div className="spec-item">
                    <h4>Alcance Hidráulico</h4>
                    <p>
                      <strong>12 metros</strong>
                    </p>
                  </div>
                </>
              )}
              {equipment.slug === 'munck-especial-e8' && (
                <>
                  <div className="spec-item">
                    <h4>Capacidade no Pé</h4>
                    <p>
                      4,5 metros: <strong>9 ton</strong>
                    </p>
                  </div>
                  <div className="spec-item">
                    <h4>Alcance Hidráulico</h4>
                    <p>
                      <strong>20 metros</strong>
                    </p>
                  </div>
                </>
              )}
              {equipment.slug === 'munck-especial-flyjib' && (
                <>
                  <div className="spec-item">
                    <h4>Capacidade no Pé</h4>
                    <p>
                      4,2 metros: <strong>12 ton</strong>
                    </p>
                  </div>
                  <div className="spec-item">
                    <h4>Alcance Hidráulico</h4>
                    <p>
                      <strong>31 metros</strong>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="equipment-graficos">
        <div className="container">
          <h2>Gráficos Operacionais</h2>
          <div className="graficos-gallery">
            <div className="grafico-viewer">
              <img
                src={equipment.graficos[currentImageIndex]}
                alt={`${equipment.name} - Gráfico ${currentImageIndex + 1}`}
                loading="lazy"
                decoding="async"
              />
              {equipment.graficos.length > 1 && (
                <div className="gallery-controls">
                  <button onClick={prevImage} className="gallery-btn prev">
                    ‹
                  </button>
                  <span className="gallery-counter">
                    {currentImageIndex + 1} de {equipment.graficos.length}
                  </span>
                  <button onClick={nextImage} className="gallery-btn next">
                    ›
                  </button>
                </div>
              )}
            </div>

            {equipment.graficos.length > 1 && (
              <div className="graficos-thumbnails">
                {equipment.graficos.map((grafico, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={grafico}
                      alt={`Gráfico ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="equipment-contact">
        <div className="container">
          <div className="contact-cta">
            <h3>Interessado neste equipamento?</h3>
            <p>Entre em contato conosco para solicitar um orçamento personalizado.</p>
            <Link to="/contato" className="contact-btn">
              Solicitar Orçamento
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default DynamicEquipmentDetail;
